import { AppProps } from "next/app";
import Router from "next/router";
import { useEffect, useState } from "react";
import { AppUpdateChecker } from "~/components/app-update-checker";
import { Clarity } from "~/components/clarity";
import { AssistanceDialog } from "~/components/assistance-dialog";
import { MaintenanceNotice } from "~/components/maintenance-notice";
import { OfflineDialog } from "~/components/offline-dialog";
import { PushNotification } from "~/components/push-notification";
import { SessionTrackerDialog } from "~/components/session-tracker-dialog";
import { useRouterEvent } from "~/libs/google-analytics";
import { AppProvider } from "~/providers/app-provider";
import { QueryProvider } from "~/providers/query-provider";
import { useIsReady } from "~/store";
import { AppToaster } from "~served/ui-comps/components/app-toaster";
import { ErrorBoundary } from "~served/ui-comps/components/error-boundary";
import { OverlayGhostHost } from "~served/ui-comps/components/overlay-presence/overlay-ghost-host";
import { SplashScreen } from "~served/ui-comps/components/splash-screen";

export default function MyApp({ Component, pageProps }: AppProps) {
	useServiceWorker();
	useRouterEvent();

	const isReady = useIsReady();
	const isMounted = useIsMounted();

	if (!isMounted) return null;

	return (
		<ErrorBoundary appName="CONSUMER APP">
			<QueryProvider>
				<AppProvider>
					<Clarity />
					<AppToaster
						position="top-center"
						visibleToasts={5}
					/>
					<OverlayGhostHost />
					<OfflineDialog />
					<AssistanceDialog />
					<SessionTrackerDialog />
					<AppUpdateChecker />
					<PushNotification />
					<MaintenanceNotice />
					{isReady ? <Component {...pageProps} /> : <SplashScreen />}
				</AppProvider>
			</QueryProvider>
		</ErrorBoundary>
	);
}

disableReactDevTools();

const useIsMounted = () => {
	const [isMounted, setIsMounted] = useState(false);

	useEffect(() => {
		setIsMounted(true);
	}, []);

	return isMounted;
};

const useServiceWorker = () => {
	useEffect(() => {
		const onReceiveServiceWorkerMessage = (event: MessageEvent) => {
			if (!event.data.action) return;

			switch (event.data.action) {
				case "redirect-from-notificationclick":
					void Router.replace(event.data.url);
					break;
			}
		};

		if (navigator.serviceWorker)
			navigator.serviceWorker.addEventListener(
				"message",
				onReceiveServiceWorkerMessage,
			);

		return () => {
			if (navigator.serviceWorker)
				navigator.serviceWorker.removeEventListener(
					"message",
					onReceiveServiceWorkerMessage,
				);
		};
	}, []);
};

function disableReactDevTools() {
	const isFunction = (obj: unknown) => {
		return typeof obj === "function";
	};

	const isObject = (obj: unknown) => {
		const type = typeof obj;
		return type === "function" || (type === "object" && !!obj);
	};

	const hasWindowObject = () => {
		return typeof window !== "undefined" && !!window.document;
	};

	if (hasWindowObject()) {
		window.DEBUG_REACT_DEV_TOOLS_ENABLED = false;
	}

	const isEnabledDevTools = process.env.NODE_ENV === "development";

	if (isEnabledDevTools) {
		if (hasWindowObject()) window.DEBUG_REACT_DEV_TOOLS_ENABLED = true;
		return;
	}

	if (hasWindowObject()) {
		if (!isObject(window.__REACT_DEVTOOLS_GLOBAL_HOOK__)) {
			return;
		}

		for (const prop in window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
			if (prop === "renderers") {
				window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop] = new Map();
				continue;
			}
			window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop] = isFunction(
				window.__REACT_DEVTOOLS_GLOBAL_HOOK__[prop],
			)
				? Function.prototype
				: null;
		}
	}
}

declare global {
	interface Window {
		DEBUG_REACT_DEV_TOOLS_ENABLED: boolean;
		__REACT_DEVTOOLS_GLOBAL_HOOK__: any;
	}
}
